import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    IconButton,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormHelperText,
    CircularProgress,
    Stack
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { CreateOrganization, UpdateOrganization, FetchParentOrganizations } from '../../../../services/api';
import usePermission from '../../../../hooks/usePermission';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

const CreateOrganizationForm = ({ onClose, onSuccess, initialData, initialParentId }) => {
    const { can, user } = usePermission();
    const isEditMode = !!initialData;

    // Permission checks
    const isGlobalUser = user && (user.organizationId === null || user.organizationId === undefined);
    const canCreateParent = can('organization.parent.create');
    const canCreateChild = can('organization.child.create');

    // Determine initial mode
    const getInitialMode = () => {
        if (isEditMode) {
            return initialData.parentId ? 'child' : 'parent';
        }
        if (initialParentId) return 'child';
        return canCreateParent ? 'parent' : 'child';
    };

    const [creationMode, setCreationMode] = useState(getInitialMode());
    const [parentOrganizations, setParentOrganizations] = useState([]);
    const [loadingOrgs, setLoadingOrgs] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Fetch parent organizations if in child mode
    useEffect(() => {
        const loadParentOrgs = async () => {
            if (creationMode === 'child') {
                setLoadingOrgs(true);
                try {
                    const token = localStorage.getItem('accessToken');
                    // Use new API to fetch ONLY parent organizations
                    const response = await FetchParentOrganizations(token);
                    if (response?.data) {
                        const orgs = Array.isArray(response.data) ? response.data : (response.data.data || []);
                        setParentOrganizations(orgs);
                    }
                } catch (error) {
                    console.error('Failed to load organizations', error);
                } finally {
                    setLoadingOrgs(false);
                }
            }
        };

        loadParentOrgs();
    }, [creationMode]);

    const validationSchema = Yup.object({
        organizationName: Yup.string()
            .trim()
            .min(2, 'Name must be at least 2 characters')
            .max(200, 'Name must not exceed 200 characters')
            .required('Organization Name is required'),
        organizationCode: Yup.string()
            .trim()
            .min(2, 'Code must be at least 2 characters')
            .max(50, 'Code must not exceed 50 characters')
            .required('Organization Code is required'),
        organizationType: Yup.string()
            .oneOf(['DE', 'Zoho', 'Non'], 'Invalid Organization Type')
            .required('Organization Type is required'),
        orgAddress: Yup.array()
            .of(Yup.string().trim())
            .min(1, 'At least one address is required'),
        gstNumber: Yup.string().nullable(),
        description: Yup.string().max(1000, 'Description too long').nullable(),
        parentId: Yup.string().when('creationMode', {
            is: (val) => creationMode === 'child',
            then: () => Yup.string().required('Parent Organization is required'),
            otherwise: () => Yup.string().nullable()
        })
    });

    const formik = useFormik({
        initialValues: {
            organizationName: initialData?.organizationName || '',
            organizationCode: initialData?.organizationCode || '',
            organizationType: initialData?.organizationType || 'DE',
            description: initialData?.description || '',
            gstNumber: initialData?.gstNumber || '',
            orgAddress: initialData?.orgAddress || (initialData?.address?.addresses?.map(a => a.addressLine1) || ['']),
            parentId: initialData?.parentId || initialParentId || (user?.organizationId || ''),
        },
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: async (values, { setSubmitting, setStatus }) => {
            const validAddresses = values.orgAddress.filter(addr => addr && addr.trim() !== '');

            const payload = {
                organizationName: values.organizationName,
                organizationCode: values.organizationCode,
                organizationType: values.organizationType,
                description: values.description,
                gstNumber: values.gstNumber,
                orgAddress: validAddresses,
                isActive: initialData ? initialData.isActive : true,
                parentId: creationMode === 'child' ? values.parentId : null
            };

            try {
                const token = localStorage.getItem('accessToken');
                let response;

                if (isEditMode) {
                    response = await UpdateOrganization(initialData.id || initialData._id, payload, token);
                } else {
                    response = await CreateOrganization(payload, token);
                }

                if (response && (response.status === 200 || response.status === 201)) {
                    if (onSuccess) onSuccess();
                    if (onClose) onClose();
                } else {
                    const errorMsg = response?.response?.data?.message || response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} organization`;
                    setStatus({ error: errorMsg });
                }
            } catch (error) {
                console.error(`Error ${isEditMode ? 'updating' : 'creating'} organization:`, error);
                setStatus({ error: 'An unexpected error occurred. Please try again.' });
            } finally {
                setSubmitting(false);
            }
        },
    });

    const handleAddressChange = (index, value) => {
        const updatedAddresses = [...formik.values.orgAddress];
        updatedAddresses[index] = value;
        formik.setFieldValue('orgAddress', updatedAddresses);
    };

    const addAddressField = () => {
        formik.setFieldValue('orgAddress', [...formik.values.orgAddress, '']);
    };

    const removeAddressField = (index) => {
        const updatedAddresses = formik.values.orgAddress.filter((_, i) => i !== index);
        formik.setFieldValue('orgAddress', updatedAddresses);
    };

    const handleModeChange = (e) => {
        setCreationMode(e.target.value);
        if (e.target.value === 'parent') {
            formik.setFieldValue('parentId', '');
        } else if (user?.organizationId) {
            formik.setFieldValue('parentId', user.organizationId);
        }
    };

    // Style for smaller inputs
    const inputStyle = {
        '& .MuiInputBase-input': { padding: '10px 14px', fontSize: '14px' },
        '& .MuiInputLabel-root': { fontSize: '14px', top: '-4px' },
        '& .MuiInputLabel-shrink': { top: '0px' }
    };

    return (
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ p: 3 }}>
            <Box display="flex" justifyContent="center" mb={1}>
                <Typography variant="h5" fontWeight="bold">
                    {isEditMode ? 'Edit Organization' : 'Create Organization'}
                </Typography>
            </Box>

            {formik.status?.error && (
                <Box mb={2} p={1} bgcolor="error.light" borderRadius={1}>
                    <Typography variant="body2" color="error" align="center">
                        {formik.status.error}
                    </Typography>
                </Box>
            )}

            <Grid container spacing={3}>
                {/* Mode Selection - Only allow switching if creating or Super Admin editing */}
                {(!isEditMode) && (canCreateParent || canCreateChild) && isGlobalUser && (
                    <Grid item xs={12}>
                        <FormControl component="fieldset">
                            <Typography variant="subtitle2" gutterBottom>Organization Category:</Typography>
                            <RadioGroup
                                row
                                name="creationMode"
                                value={creationMode}
                                onChange={handleModeChange}
                            >
                                {canCreateParent && (
                                    <FormControlLabel
                                        value="parent"
                                        control={<Radio size="small" />}
                                        label="Parent Organization"
                                        disabled={isEditMode}
                                    />
                                )}
                                {canCreateChild && (
                                    <FormControlLabel
                                        value="child"
                                        control={<Radio size="small" />}
                                        label="Child Organization"
                                        disabled={isEditMode}
                                    />
                                )}
                            </RadioGroup>
                        </FormControl>
                    </Grid>
                )}

                {/* Parent Selector */}
                {creationMode === 'child' && (
                    <Grid item xs={12}>
                        <FormControl fullWidth size="small" error={formik.touched.parentId && Boolean(formik.errors.parentId)}>
                            <InputLabel id="parent-select-label">Parent Organization</InputLabel>
                            <Select
                                labelId="parent-select-label"
                                id="parentId"
                                name="parentId"
                                value={formik.values.parentId}
                                label="Parent Organization"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                disabled={!!user?.organizationId || isEditMode}
                                sx={{ ...inputStyle, height: '40px' }}
                            >
                                {loadingOrgs ? (
                                    <MenuItem disabled><CircularProgress size={20} /></MenuItem>
                                ) : (
                                    parentOrganizations.map(org => (
                                        <MenuItem key={org.id || org._id} value={org.id || org._id}>
                                            {org.organizationName}
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                            {formik.touched.parentId && formik.errors.parentId && (
                                <FormHelperText>{formik.errors.parentId}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>
                )}

                <Grid item xs={12} sm={6}>
                    <TextField
                        required
                        fullWidth
                        size="small"
                        id="organizationName"
                        name="organizationName"
                        label="Organization Name"
                        value={formik.values.organizationName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.organizationName && Boolean(formik.errors.organizationName)}
                        helperText={formik.touched.organizationName && formik.errors.organizationName}
                        sx={inputStyle}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        required
                        fullWidth
                        size="small"
                        id="organizationCode"
                        name="organizationCode"
                        label="Organization Code"
                        value={formik.values.organizationCode}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.organizationCode && Boolean(formik.errors.organizationCode)}
                        helperText={formik.touched.organizationCode && formik.errors.organizationCode}
                        sx={inputStyle}
                    />
                </Grid>

                {/* Organization Type - Changed to Select as requested */}
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small" error={formik.touched.organizationType && Boolean(formik.errors.organizationType)}>
                        <InputLabel id="org-type-label">Organization Type</InputLabel>
                        <Select
                            labelId="org-type-label"
                            id="organizationType"
                            name="organizationType"
                            value={formik.values.organizationType}
                            label="Organization Type"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            sx={{ ...inputStyle, height: '40px' }}
                        >
                            <MenuItem value="DE">DE</MenuItem>
                            <MenuItem value="Zoho">Zoho</MenuItem>
                            <MenuItem value="Non">Non</MenuItem>
                        </Select>
                        {formik.touched.organizationType && formik.errors.organizationType && (
                            <FormHelperText>{formik.errors.organizationType}</FormHelperText>
                        )}
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        size="small"
                        id="gstNumber"
                        name="gstNumber"
                        label="GST Number"
                        value={formik.values.gstNumber}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        sx={inputStyle}
                        placeholder="Optional"
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        size="small"
                        id="description"
                        name="description"
                        label="Description"
                        value={formik.values.description}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        sx={inputStyle}
                        placeholder="Optional description"
                    />
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary' }}>Organization Addresses</Typography>
                    {formik.values.orgAddress.map((addr, index) => (
                        <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                            <TextField
                                fullWidth
                                size="small"
                                label={`Address ${index + 1}`}
                                value={addr}
                                onChange={(e) => handleAddressChange(index, e.target.value)}
                                sx={inputStyle}
                            />
                            <IconButton
                                onClick={() => removeAddressField(index)}
                                color="error"
                                size="small"
                                disabled={formik.values.orgAddress.length === 1 && index === 0}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    ))}
                    <Button
                        size="small"
                        variant="text"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={addAddressField}
                    >
                        Add Another Address
                    </Button>
                </Grid>

                <Grid item xs={12}>
                    <Stack direction="row" spacing={2} justifyContent="center" mt={2}>
                        <Button variant="outlined" onClick={onClose} disabled={submitting}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={submitting || !formik.isValid}
                            startIcon={submitting && <CircularProgress size={20} color="inherit" />}
                        >
                            {submitting ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Organization' : 'Create Organization')}
                        </Button>
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    );
};

export default CreateOrganizationForm;
